import { ServiceTypeEnum } from "bonusx-api-main-manager";
import isEmpty from "lodash/isEmpty";
import type { IProductWithCmsDataAndQualification } from "../../pages/ProductsListPage/entities/product-with-cms-data-and-qualification/product-with-cms-data-and-qualification.entity.interface";
import { ProductRequisitesService } from "../ProductRequisitesService/ProductRequisitesService.service";
import type { IProductSearchService } from "./interfaces/ProductSearchService.interface";
import type { IProductWithPurchasingInformation } from "./interfaces/ProductWithPurchasingInformation.interface";
import type { ISearchResult } from "./interfaces/SearchResult.interface";
import type { ServiceByTypology } from "./interfaces/ServiceByTypology.interface";

/**
 * @deprecated
 * TODO - this logic should be moved from the front-end to the back-end in order to shift the
 * business logic and computational costs on the server-side and reduce te impact on users in terms
 * of performance
 */
export class ProductSearchService implements IProductSearchService {
  constructor(private readonly _productRequisiteService = new ProductRequisitesService()) {}

  arrangeFilteredProducts<T extends IProductWithCmsDataAndQualification, R extends ISearchResult>(
    filteredServices: T[],
    uniqueFilteredServices: T[],
    tagsName: string[],
    sectionPercetanges: number[]
  ): R {
    let results: ISearchResult = this._searchServices(
      filteredServices,
      uniqueFilteredServices,
      tagsName,
      sectionPercetanges
    );

    return results as R;
  }

  private _searchServices(
    products: IProductWithCmsDataAndQualification[],
    uniqueProducts: IProductWithCmsDataAndQualification[],
    tagsName: string[],
    sectionPercetanges: number[]
  ): ISearchResult {
    if (isEmpty(tagsName)) {
      const servicesByTypologies = this._classifyServicesByTypologies(products, sectionPercetanges);
      return servicesByTypologies;
    }

    const servicesByTypologies = this._classifyServicesByTypologies(uniqueProducts, sectionPercetanges);
    return servicesByTypologies;
  }

  private _classifyServicesByTypologies(
    services: IProductWithCmsDataAndQualification[],
    sectionPercetanges: number[]
  ): ISearchResult {
    const servicesWithPurchasingInformation: IProductWithPurchasingInformation[] =
      this._decorateServicesWithPurchaseInformation(services, sectionPercetanges);

    const servicesByTypologies = this._reduceServicesWithInformationIntoTypologies(servicesWithPurchasingInformation);
    return servicesByTypologies;
  }

  private _decorateServicesWithPurchaseInformation(
    services: IProductWithCmsDataAndQualification[],
    sectionPercetanges: number[]
  ): IProductWithPurchasingInformation[] {
    const servicesWithPurchasingInformation: IProductWithPurchasingInformation[] = services.map((service) => {
      const { canPurchaseService } = this._productRequisiteService.getPurchasingInformation(
        service,
        sectionPercetanges
      );

      return { ...service, canPurchaseService };
    });

    return servicesWithPurchasingInformation;
  }

  private _reduceServicesWithInformationIntoTypologies(
    servicesWithPurchasingInformation: IProductWithPurchasingInformation[]
  ): ServiceByTypology {
    const servicesByTypologies = servicesWithPurchasingInformation.reduce(
      (
        servicesByTypologies: ServiceByTypology,
        currentService: IProductWithPurchasingInformation
      ): ServiceByTypology => {
        if (this._isTypeInfoService(currentService)) {
          this._classifyTypeInfoService(servicesByTypologies, currentService);
        } else {
          this._classifyTypeNotInfoService(servicesByTypologies, currentService);
        }
        return servicesByTypologies;
      },
      {
        purchasableServices: [],
        needToUpdateProfileServices: [],
        needToCompleteInformationsServices: [],
      }
    );
    return servicesByTypologies;
  }

  private _classifyTypeNotInfoService(
    accumulator: ServiceByTypology,
    currentService: IProductWithPurchasingInformation
  ): void {
    if (!this._isAvailableService(currentService)) {
      if (this._isPurchasableService(currentService)) {
        accumulator.needToUpdateProfileServices.push(currentService);
      } else {
        accumulator.needToCompleteInformationsServices.push(currentService);
      }
    } else if (this._isPurchasableService(currentService)) {
      accumulator.purchasableServices.push(currentService);
    } else {
      accumulator.needToCompleteInformationsServices.push(currentService);
    }
  }

  private _classifyTypeInfoService(
    servicesByTypologies: ServiceByTypology,
    currentService: IProductWithPurchasingInformation
  ): void {
    if (this._isAvailableService(currentService)) {
      servicesByTypologies.purchasableServices.push(currentService);
    } else if (this._isPurchasableService(currentService)) {
      servicesByTypologies.needToUpdateProfileServices.push(currentService);
    } else {
      servicesByTypologies.needToCompleteInformationsServices.push(currentService);
    }
  }

  private _isTypeInfoService(service: IProductWithCmsDataAndQualification): boolean {
    return service.type === ServiceTypeEnum.Info;
  }

  private _isAvailableService(service: IProductWithCmsDataAndQualification): boolean {
    return service.available;
  }

  private _isPurchasableService(service: IProductWithPurchasingInformation): boolean {
    return service.canPurchaseService;
  }
}
